import { defineStore } from 'pinia'
import { useCookies } from 'vue3-cookies'
import { ApiDeleevConfig } from '@/api'
import ApiDeleevAuth from '@/api/deleev/auth'
import {
  ADMIN_GROUP,
  SUPPORT_GROUP,
  SUPPORT_ADMIN_GROUP,
  CRISP_MONITORING_PERMISSION,
  CONVERSATIONS_STATS_PERMISSION,
  CRISP_UNLOCK_EMAIL_PERMISSION,
  CAN_ACCESS_EXTERNAL_REVIEW_CODENAME,
  CAN_UNLOCK_EXTERNAL_REVIEW_CODENAME,
  AI_PIPELINE_TEST_PERMISSION,
  AI_PIPELINE_TEST_ALLOWED_EMAILS,
} from '@/utils/permissions'

const { cookies } = useCookies()

const PERMISSION_MAP = {
  [ADMIN_GROUP]: 'grantAdmin',
  [SUPPORT_GROUP]: 'grantSupport',
  [SUPPORT_ADMIN_GROUP]: 'grantSupportAdmin',
  [CRISP_MONITORING_PERMISSION]: 'grantCrispMonitoring',
  [CONVERSATIONS_STATS_PERMISSION]: 'grantConversationsStats',
  [CRISP_UNLOCK_EMAIL_PERMISSION]: 'grantUnlockEmail',
  [CAN_ACCESS_EXTERNAL_REVIEW_CODENAME]: 'grantExternalReview',
  [CAN_UNLOCK_EXTERNAL_REVIEW_CODENAME]: 'grantUnlockExternalReview',
  [AI_PIPELINE_TEST_PERMISSION]: 'grantAiPipelineTest',
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: '',
    user: {},
  }),
  getters: {
    grants: (state) =>
      state.user.grants?.filter((p) => p.type === 'perm' && p.value).map((p) => p.codename) ?? [],
    isAuthenticated: (state) => !!state.token && !!state.user.id,
    grantSuperAdmin: (state) => state.user.is_superuser,
    isGroupSuperAdmin: (state) => state.user.groups?.is_super_admin,
    grantAdmin(state) {
      return this.grantSuperAdmin || state.user.groups?.is_admin
    },
    grantSupport(state) {
      return this.grantAdmin || state.user.groups?.is_support
    },
    grantSupportAdmin(state) {
      return this.grantSuperAdmin || state.user.groups?.is_support_admin
    },
    grantCrispMonitoring() {
      return (
        this.grantSuperAdmin ||
        this.grantSupport ||
        this.grants.includes(CRISP_MONITORING_PERMISSION)
      )
    },
    grantConversationsStats() {
      return (
        this.grantSuperAdmin ||
        this.grantSupportAdmin ||
        this.grants.includes(CONVERSATIONS_STATS_PERMISSION)
      )
    },
    grantUnlockEmail() {
      return (
        this.grantSuperAdmin ||
        this.grantSupportAdmin ||
        this.grants.includes(CRISP_UNLOCK_EMAIL_PERMISSION)
      )
    },
    grantExternalReview() {
      return (
        this.grantSuperAdmin ||
        this.grantSupport ||
        this.grants.includes(CAN_ACCESS_EXTERNAL_REVIEW_CODENAME)
      )
    },
    grantUnlockExternalReview() {
      return (
        this.grantSuperAdmin ||
        this.grantSupportAdmin ||
        this.grants.includes(CAN_UNLOCK_EXTERNAL_REVIEW_CODENAME)
      )
    },
    // Liste blanche d'emails (page Test pipeline IA), indépendante des groupes.
    grantAiPipelineTest: (state) =>
      AI_PIPELINE_TEST_ALLOWED_EMAILS.includes((state.user.email || '').toLowerCase()),
    isPlanningRestricted() {
      return this.user.staff_info.is_planning_restricted
    },
  },
  actions: {
    getStoredToken() {
      const _token = cookies.get('api_token')
      if (_token && _token !== 'undefined') return _token

      return ''
    },
    destroyToken() {
      this.$reset()
      cookies.remove('api_token', '/', import.meta.env.VITE_COOKIE_DOMAIN)
      ApiDeleevConfig.removeAuthorizationToken()
    },
    async checkToken() {
      if (this.isAuthenticated) return

      const token = this.getStoredToken()
      if (!token) return
      try {
        const data = await ApiDeleevAuth.checkToken(token)
        if (data && data.is_staff) {
          this.token = token
          this.user = data
          ApiDeleevConfig.setAuthorizationToken(token)
        }
      } catch (e) {
        return { error: e }
      }
      return { error: null }
    },
    async login({ email, password }) {
      try {
        const loginData = await ApiDeleevAuth.login({ email, password })
        if (loginData && loginData.auth_token) {
          const userData = await ApiDeleevAuth.checkToken(loginData.auth_token)
          if (userData && userData.is_staff) {
            this.user = userData
            this.token = loginData.auth_token
            ApiDeleevConfig.setAuthorizationToken(this.token)
            cookies.set(
              'api_token',
              loginData.auth_token,
              '30d',
              '/',
              import.meta.env.VITE_COOKIE_DOMAIN,
              true,
              'None',
            )
          }
        } else {
          return { error: 'Impossible de se connecter' }
        }
      } catch (e) {
        console.error(e)
        return { error: "Erreur lors de l'authentification" }
      }
      return { error: null }
    },
    logout() {
      this.destroyToken()
    },
    hasPermission(codename) {
      const authKey = PERMISSION_MAP[codename]
      return authKey ? this[authKey] : false
    },
  },
})
